import { faFlagCheckered } from '@fortawesome/free-solid-svg-icons';
import type { JSX } from 'react';
import { memo, useMemo } from 'react';

import { ActivityLogEvent } from '@/constants/activityLogEvent';
import TimelineHeader from '@/pages/Timeline/components/TimelineHeader/TimelineHeader';
import TimestampAndActor from '@/pages/Timeline/components/TimestampAndActor/TimestampAndActor';
import { TimelineSubcomponentProps } from '@/pages/Timeline/types';
import Card from '@/ui/components/Card';

// const programQualificationDataExample = {
//   substance: 'Alcohol',
//   actor_email: 'example@example.com',
//   payment_type: 'insurance',
//   program_name: 'Alcohol Program - Standard',
//   qualified_at_datetime: '2023-07-12T09:44:47.535+00:00'
// };

// const disqualifiedDataExample = {
//   substance: 'Cocaine',
//   actor_email: 'mauricio.barg@affecttherapeutics.com',
//   payment_type: 'insurance',
//   program_name: 'Stimulant Program - Standard',
//   disqualified_by: 'mauricio.barg@affecttherapeutics.com',
//   disqualification_reasons: ['AP-1973'],
//   disqualified_at_datetime: '2023-07-20T16:15:25.170+00:00'
// };

// const intakeRejectedDataExample = {
//   substance: 'Meth',
//   actor_email: 'robert.gacek+39@monterail.com',
//   payment_type: 'insurance',
//   program_name: 'Stimulant Program - Standard',
//   intake_rejection_by: 'robert.gacek+39@monterail.com',
//   intake_rejection_reasons: ['Is not fluent in English / not able to participate'],
//   intake_rejection_at_datetime: '2023-07-12T12:42:25.656+00:00'
// };

const ProgramQualificationEvent = ({ activityLog }: TimelineSubcomponentProps): JSX.Element => {
  const { action, actor, data, timestamp } = activityLog;

  const content = useMemo(() => {
    switch (action) {
      case ActivityLogEvent.program_qualification:
      case ActivityLogEvent.PROGRAM_QUALIFICATION_EVENT:
        return (
          <TimelineHeader
            icon={faFlagCheckered}
            iconClassName="text-green-500"
            subtitle={data?.program_name}
            title="Program Qualification Created"
          />
        );

      case ActivityLogEvent.program_qualification_disqualified:
      case ActivityLogEvent.PROGRAM_QUALIFICATION_DISQUALIFIED_EVENT:
        return (
          <>
            <TimelineHeader
              icon={faFlagCheckered}
              iconClassName="text-red-500"
              title="Lead Disqualified"
            />

            {data?.disqualification_reasons && data?.disqualification_reasons.length && (
              <>
                {data.disqualification_reasons.map(
                  (disqualificationReason: string, index: number) => {
                    return (
                      <div key={`${disqualificationReason}-${index}`}>{disqualificationReason}</div>
                    );
                  }
                )}
              </>
            )}
          </>
        );

      case ActivityLogEvent.program_qualification_intake_rejected:
      case ActivityLogEvent.PROGRAM_QUALIFICATION_INTAKE_REJECTED_EVENT:
        return (
          <>
            <TimelineHeader
              icon={faFlagCheckered}
              iconClassName="text-yellow-500"
              title="Lead Intake Rejected"
            />

            {data?.intake_rejection_reasons && data?.intake_rejection_reasons.length && (
              <>
                {data.intake_rejection_reasons.map(
                  (intakeRejectionReason: string, index: number) => {
                    return (
                      <div key={`${intakeRejectionReason}-${index}`}>{intakeRejectionReason}</div>
                    );
                  }
                )}
              </>
            )}
          </>
        );

      default:
        return action ?? 'Program Qualification Event';
    }
  }, [action, data?.disqualification_reasons, data?.intake_rejection_reasons, data?.program_name]);

  return (
    <div>
      <Card>{content}</Card>

      <TimestampAndActor actor={actor} timestamp={timestamp} />
    </div>
  );
};

export default memo(ProgramQualificationEvent);
